import { Icon } from "@iconify/react";
import { useEffect, useRef } from "react";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerProvider,
  MessageScrollerViewport,
  useMessageScroller,
} from "@/components/ui/message-scroller";
import { useI18n } from "@/locales/i18n-provider";
import type { TicketActivityItem } from "../utils/ticket-presentation";
import { TicketActivity } from "./ticket-activity";

function ScrollToEndOnRequest({ request }: { request: number }) {
  const { scrollToEnd } = useMessageScroller();
  const handledRequestRef = useRef(0);

  useEffect(() => {
    if (request === 0 || request === handledRequestRef.current) return;
    handledRequestRef.current = request;
    scrollToEnd({ behavior: "smooth" });
  }, [request, scrollToEnd]);

  return null;
}

export function TicketActivityScroller({
  activities,
  scrollToEndRequest,
  ticketId,
}: {
  activities: TicketActivityItem[];
  scrollToEndRequest: number;
  ticketId: string;
}) {
  const { t } = useI18n();

  return (
    <MessageScrollerProvider autoScroll defaultScrollPosition="end">
      <MessageScroller className="h-auto overflow-auto lg:h-full lg:overflow-hidden">
        <ScrollToEndOnRequest request={scrollToEndRequest} />
        <MessageScrollerViewport className="h-auto overflow-auto px-4 py-2 lg:h-full lg:overflow-y-auto">
          <MessageScrollerContent className="gap-0">
            {activities.length > 0 ? (
              <TicketActivity activities={activities} ticketId={ticketId} />
            ) : (
              <Empty className="py-10">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Icon className="size-5" icon="tabler:message-off" />
                  </EmptyMedia>
                  <EmptyTitle>{t("tickets.activity.emptyTitle")}</EmptyTitle>
                  <EmptyDescription>{t("tickets.activity.emptyDescription")}</EmptyDescription>
                </EmptyHeader>
              </Empty>
            )}
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton
          aria-label={t("tickets.activity.scrollToBottomAria")}
          className="hidden rounded-full lg:inline-flex"
          direction="end"
          title={t("tickets.activity.scrollToBottom")}
          variant="outline"
        />
      </MessageScroller>
    </MessageScrollerProvider>
  );
}
